import React from "react";
import { Navigate, Route } from "react-router-dom";
import type { RouteCtx } from "../routeContext";
import { PlaceholderPage, NotFound } from "../shell";
import {
  SponsorDashboard,
  StudyManagement,
  SiteManagement,
  MonitoringTracker,
  MonitoringOversightDashboard,
  EcrfViewerPage,
  StudyMilestones,
  SiteActivation,
  RegulatoryApprovalsMain,
  ApprovalDetailPage,
  DatasetExport,
  CTMSReportsHub,
  IssueRiskTrackerPage,
  SiteFeasibilityPage,
  CDAManagement,
  RBQMCenter,
} from "../lazyPages";

/** sponsorRoutes — order preserved for React Router first-match. */
export function sponsorRoutes(ctx: RouteCtx): React.ReactNode {
  const {
    userRole,
    selectedStudyId,
    availableStudies,
    hasRbqmAccess,
    currentStudy,
  } = ctx;

  return (
    <>
      {/* ── 3a. UNGATED path tree: /sponsor/* (any authenticated role) ─── */}
      {/* Sponsor Routes - All sponsor-specific paths */}
      <Route
        path="/sponsor/portfolio"
        element={
          <SponsorDashboard currentStudy={currentStudy} userRole={userRole} />
        }
      />
      <Route path="/sponsor/study/dashboard" element={<StudyMilestones />} />
      <Route
        path="/sponsor/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route path="/sponsor/study-milestones" element={<StudyMilestones />} />

      {/* Sponsor Analytics & Reports */}
      <Route
        path="/sponsor/analytics/enrollment"
        element={<PlaceholderPage title="Enrollment & Timeline" />}
      />
      <Route
        path="/sponsor/analytics/sites"
        element={<PlaceholderPage title="Site Performance" />}
      />
      <Route
        path="/sponsor/analytics/quality"
        element={<PlaceholderPage title="Data Quality / Queries KPI" />}
      />
      <Route
        path="/sponsor/analytics/exports"
        element={<PlaceholderPage title="Export Reports (PDF/CSV)" />}
      />
      <Route
        path="/sponsor/analytics/reports"
        element={
          <CTMSReportsHub userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/sponsor/monitoring/oversight"
        element={<MonitoringOversightDashboard currentStudy={currentStudy} />}
      />

      {/* Sponsor Finance & Payments */}
      <Route
        path="/sponsor/finance/budget"
        element={<PlaceholderPage title="Budget Overview" />}
      />
      <Route
        path="/sponsor/finance/milestones"
        element={<PlaceholderPage title="Milestones & Payouts" />}
      />
      <Route
        path="/sponsor/finance/invoices"
        element={<PlaceholderPage title="Invoices" />}
      />
      <Route
        path="/sponsor/finance/contracts"
        element={<PlaceholderPage title="Contracts" />}
      />

      {/* Sponsor CTMS Oversight */}
      <Route
        path="/sponsor/ctms/study"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/sponsor/ctms/sites"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />
      <Route
        path="/sponsor/ctms/monitoring"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/sponsor/regulatory"
        element={
          <RegulatoryApprovalsMain
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/sponsor/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />
      <Route
        path="/sponsor/site-activation"
        element={
          <SiteActivation userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/sponsor/site-feasibility"
        element={
          <SiteFeasibilityPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/sponsor/rbqm-center"
        element={
          hasRbqmAccess ? (
            <RBQMCenter userRole={userRole} currentStudy={currentStudy} />
          ) : (
            <NotFound />
          )
        }
      />
      <Route
        path="/sponsor/cda-management"
        element={
          <CDAManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />

      {/* Sponsor EDC & Data */}
      <Route path="/sponsor/edc/viewer" element={<EcrfViewerPage />} />
      <Route
        path="/sponsor/edc/freeze-status"
        element={<PlaceholderPage title="Data Freeze Status" />}
      />
      <Route path="/sponsor/edc/export-request" element={<DatasetExport />} />

      {/* Sponsor Risk & Safety */}
      <Route
        path="/sponsor/risk/sae"
        element={<PlaceholderPage title="SAE Summary" />}
      />
      <Route
        path="/sponsor/risk/deviations"
        element={<PlaceholderPage title="Protocol Deviations Summary" />}
      />
      <Route
        path="/sponsor/risk/register"
        element={
          <IssueRiskTrackerPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/sponsor/risk-tracker"
        element={
          <IssueRiskTrackerPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />

      {/* Sponsor Audit — redirect to central audit logs */}
      <Route
        path="/sponsor/audit/logs"
        element={<Navigate to="/admin/audit-logs" replace />}
      />
    </>
  );
}
